import type { ChapterDto, PaginatedListOfChapterDto } from "../../models/models"
import type { PagingActions, PagingOptions } from "../../models/types"
import {
  getAllChapters,
  getChapter,
  getChaptersByLicenceIds,
  postChapter,
  putChapter,
  removeChapter
} from "../../services/ChapterService"
import { getQueryString } from "../../utils"
import type { AppState } from "../app.state"
import type { AppThunkAction, AppThunkDispatch } from "../app.types"
import { history } from "./../Store"
import type { Chapter, ChapterAction, ChapterListAction } from "./chapter.types"

export const CHAPTER_CREATE: string = "CHAPTER_CREATE"

export const CHAPTER_FETCH_LIST: string = "CHAPTER_FETCH_LIST"
export const CHAPTER_FETCH: string = "CHAPTER_FETCH"

export const CHAPTER_UPDATE: string = "CHAPTER_UPDATE"

export const CHAPTER_DELETE: string = "CHAPTER_DELETE"

export const CHAPTER_LOAD_DEFAULT: string = "CHAPTER_LOAD_DEFAULT"
export const CHAPTER_LOAD_LIST: string = "CHAPTER_LOAD_LIST"
export const CHAPTER_LOAD: string = "CHAPTER_LOAD"

export const CHAPTER_CHANGE_PAGE: string = "CHAPTER_CHANGE_PAGE"
export const CHAPTER_LOAD_LIST_OPTIONS: string = "CHAPTER_LOAD_LIST_OPTIONS"

export function createChapter(chapter: Chapter): AppThunkAction<void, ChapterAction> {
  return async (dispatch: AppThunkDispatch<ChapterAction>) => {
    dispatch({ type: CHAPTER_CREATE })
    const newChapterId: string = await postChapter(chapter)
    const newChapter: ChapterDto = await getChapter(newChapterId)

    history.replace(`/manage/chapters/${newChapterId}`)
    dispatch(loadChapter(newChapter))
  }
}

export function fetchChapterListByLicenceIds(licenceIds: string[]): AppThunkAction<void, ChapterListAction> {
  return async (dispatch: AppThunkDispatch<ChapterListAction>) => {
    dispatch({ type: CHAPTER_FETCH_LIST })

    const chapters: ChapterDto[] = await getChaptersByLicenceIds(licenceIds)

    dispatch(loadChapterList(chapters))
  }
}

export function fetchChapterList(
  pageIndex = 0,
  pageSize = 50
): AppThunkAction<void, ChapterListAction | PagingActions> {
  return async (dispatch: AppThunkDispatch<ChapterListAction | PagingActions>, getState: () => AppState) => {
    dispatch({ type: CHAPTER_FETCH_LIST })

    const state: AppState = getState()
    const queryString: string = getQueryString(pageIndex, pageSize, state.contentManager.sort)

    const pagedList: PaginatedListOfChapterDto | undefined = await getAllChapters(queryString)

    if (pagedList) {
      const pagingOptions: PagingOptions = {
        currentPage: pagedList.pageIndex,
        totalPages: pagedList.totalPages,
        pageSize: pageSize
      }

      dispatch(loadChapterList(pagedList.items as ChapterDto[]))
      dispatch({
        type: CHAPTER_LOAD_LIST_OPTIONS,
        payload: pagingOptions
      })
    }
  }
}

export function fetchChapter(id: string): AppThunkAction<void, ChapterAction> {
  return async (dispatch: AppThunkDispatch<ChapterAction>) => {
    dispatch({ type: CHAPTER_FETCH })

    const chapter: ChapterDto = await getChapter(id)

    dispatch(loadChapter(chapter))
  }
}

export function updateChapter(chapter: Chapter): AppThunkAction<void, ChapterAction> {
  return async (dispatch: AppThunkDispatch<ChapterAction>) => {
    dispatch({ type: CHAPTER_UPDATE })
    await putChapter(chapter)
    const updatedChapter: ChapterDto = await getChapter(chapter.id as string)
    dispatch(loadChapter(updatedChapter))
  }
}

export function deleteChapter(id: string): AppThunkAction<void, ChapterAction> {
  return async (dispatch: AppThunkDispatch<ChapterAction>) => {
    dispatch({ type: CHAPTER_DELETE })
    await removeChapter(id)
    dispatch(fetchChapterList())
  }
}

export function changeChapterPage(page: number): AppThunkAction<void, PagingActions> {
  return (dispatch: AppThunkDispatch<PagingActions>, getState: () => AppState) => {
    const state = getState()
    dispatch({ type: CHAPTER_CHANGE_PAGE, payload: page })

    dispatch(fetchChapterList(page, state.chapters.pagingOptions.pageSize))
  }
}

export function loadDefaultChapter(): ChapterAction {
  return {
    type: CHAPTER_LOAD_DEFAULT
  }
}

export function loadChapterList(dtoList: ChapterDto[]): ChapterListAction {
  const chapterList: Chapter[] = dtoList.map(chapterDtoToChapter)
  return {
    type: CHAPTER_LOAD_LIST,
    payload: chapterList
  }
}

export function loadChapter(dto: ChapterDto): ChapterAction {
  const chapter: Chapter = chapterDtoToChapter(dto)
  return {
    type: CHAPTER_LOAD,
    payload: chapter
  }
}

function chapterDtoToChapter(dto: ChapterDto): Chapter {
  const licenceIds: string[] | undefined = dto.licences?.map(({ id }) => id)
  return {
    ...dto,
    id: dto.id,
    name: dto.name as string,
    thumbnailUrl: dto.thumbnailUrl as string,
    licenceIds: licenceIds ? licenceIds : []
  }
}
